import {
  hashKey,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { get, post } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import {
  API_PAGE_SIZE,
  API_PRODUCT_TYPE,
  ProductData,
  ProductsPageFilter,
  ProductsPageRequest,
  ProductsPageResponse,
  ResourceTagsResponse,
} from '../types';

async function fetchProductsPage(
  apiUrl: string,
  idToken: string,
  body: ProductsPageRequest,
): Promise<ProductsPageResponse> {
  return post<ProductsPageResponse>('/resources/page', body, apiUrl, idToken);
}

function buildPageRequest({
  page,
  limit,
  search,
  tags,
  testableOnly,
}: {
  page: number;
  limit: number;
  search?: string;
  tags?: string[];
  testableOnly?: boolean;
}): ProductsPageRequest {
  const trimmedSearch = search?.trim();
  const filter: ProductsPageFilter = {
    types: [API_PRODUCT_TYPE], // Always include API type
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(tags && tags.length > 0 ? { tags } : {}),
    ...(testableOnly ? { isAccessGranted: true } : {}),
  };

  return {
    page,
    limit,
    filter,
  };
}

const PRODUCTS_PAGE_KEY = ['products', 'page'];

export interface UseProductsPageParams {
  search: string;
  tags: string[];
  testableOnly?: boolean;
  pageSize?: number;
}

export const useProductsPage = ({
  search,
  tags,
  testableOnly = false,
  pageSize = API_PAGE_SIZE,
}: UseProductsPageParams) => {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials(
    '/resource-broker/api/v2',
  );
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => [...PRODUCTS_PAGE_KEY, { search, tags, testableOnly, pageSize }],
    [search, tags, testableOnly, pageSize],
  );

  const query = useInfiniteQuery<ProductsPageResponse, Error>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const idToken = isSignedIn ? await getIdToken() : '';
      return fetchProductsPage(
        apiUrl,
        idToken,
        buildPageRequest({
          page: pageParam as number,
          limit: pageSize,
          search,
          tags,
          testableOnly,
        }),
      );
    },
    initialPageParam: 0,
    getNextPageParam: lastPage =>
      lastPage.isLast ? undefined : lastPage.page + 1,
    placeholderData: keepPreviousData,
    enabled: !!apiUrl,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });

  // Loaded pages are cached per filter combination, so re-applying an earlier
  // one would restore every page it had loaded. Dropping the inactive entries
  // keeps each filter change starting from the first page.
  const activeKeyHash = hashKey(queryKey);
  useEffect(() => {
    queryClient.removeQueries({
      queryKey: PRODUCTS_PAGE_KEY,
      predicate: cached => hashKey(cached.queryKey) !== activeKeyHash,
    });
  }, [activeKeyHash, queryClient]);

  const products = useMemo(
    () => query.data?.pages.flatMap(page => page.elements) ?? [],
    [query.data?.pages],
  );

  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...query,
    products,
    total,
    showLoadMore: Boolean(query.hasNextPage),
  };
};

export const useApiFacets = () => {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials(
    '/resource-broker/api/v2',
  );

  const query = useQuery<ResourceTagsResponse, Error>({
    queryKey: ['products', 'tags'],
    queryFn: async () => {
      const idToken = isSignedIn ? await getIdToken() : '';
      return get<ResourceTagsResponse>('/resources/tags', apiUrl, idToken);
    },
    enabled: !!apiUrl,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });

  const options = useMemo(
    () =>
      (query.data?.elements ?? [])
        .filter(item => item.tag && item.tag !== 'Documentation')
        .map(({ tag, count }) => ({ id: tag, label: tag, qty: count }))
        .sort((a, b) => b.qty - a.qty),
    [query.data?.elements],
  );

  return { ...query, options };
};

export const useProduct = (id: string) => {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials(
    '/resource-broker/api/v2',
  );

  return useQuery<ProductData, Error>({
    queryKey: ['product', id],
    queryFn: async () => {
      const idToken = await getIdToken();
      return get<ProductData>(`/resources/${id}`, apiUrl, idToken);
    },
    enabled: !!id && !!apiUrl && isSignedIn,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
};
